'use client'

import { motion } from 'framer-motion'
import {
  TrendingUp,
  Clock,
  DollarSign,
  ShoppingCart,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { Container } from '@/components/ui/container'
import { Section } from '@/components/ui/section'

const iconMap: Record<string, LucideIcon> = {
  TrendingUp,
  Clock,
  DollarSign,
  ShoppingCart,
  Users,
}

interface StatItem {
  value: string
  label: string
  description: string
  icon?: string
  positive?: boolean // true = green (increase), false = red (decrease is good, like abandonment)
}

interface StatsGridProps {
  title: string
  subtitle?: string
  stats: StatItem[]
  variant?: 'primary' | 'ai'
  columns?: 2 | 3 | 4 | 5
}

export function StatsGrid({
  title,
  subtitle,
  stats,
  variant = 'primary',
  columns = 4,
}: StatsGridProps) {
  const isAi = variant === 'ai'

  const gridCols = {
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-3',
    4: 'md:grid-cols-2 lg:grid-cols-4',
    5: 'md:grid-cols-2 lg:grid-cols-5',
  }

  return (
    <Section background={isAi ? 'dark' : 'gray'}>
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className={`text-display-1 mb-4 ${
            isAi ? 'text-white' : 'text-stone-900'
          }`}>
            {title}
          </h2>
          {subtitle && (
            <p className={`text-lg max-w-2xl mx-auto ${
              isAi ? 'text-stone-300' : 'text-stone-600'
            }`}>
              {subtitle}
            </p>
          )}
        </motion.div>

        <div className={`grid ${gridCols[columns]} gap-6`}>
          {stats.map((stat, index) => {
            const IconComponent = stat.icon ? iconMap[stat.icon] : TrendingUp
            const isPositive = stat.positive !== false

            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className={`rounded-2xl p-6 text-center ${
                  isAi
                    ? 'bg-stone-900 border border-stone-800'
                    : 'bg-white shadow-sm border border-stone-100'
                }`}
              >
                {IconComponent && (
                  <div
                    className={`inline-flex p-3 rounded-xl mb-4 ${
                      isAi
                        ? 'bg-stone-800 text-green-400'
                        : 'bg-primary-100 text-primary-600'
                    }`}
                  >
                    <IconComponent className="w-6 h-6" />
                  </div>
                )}
                <div
                  className={`text-4xl md:text-5xl font-bold mb-2 tabular-nums ${
                    isPositive
                      ? isAi
                        ? 'text-green-400'
                        : 'text-primary-600'
                      : 'text-emerald-600'
                  }`}
                >
                  {stat.value}
                </div>
                <div className={`font-semibold mb-1 ${
                  isAi ? 'text-white' : 'text-stone-900'
                }`}>
                  {stat.label}
                </div>
                <p className={`text-sm ${isAi ? 'text-stone-400' : 'text-stone-500'}`}>
                  {stat.description}
                </p>
              </motion.div>
            )
          })}
        </div>
      </Container>
    </Section>
  )
}

// Compact horizontal stats for inline use
interface CompactStatsProps {
  stats: Array<{ value: string; label: string }>
  variant?: 'primary' | 'ai'
}

export function CompactStats({ stats, variant = 'primary' }: CompactStatsProps) {
  const isAi = variant === 'ai'

  return (
    <div className="flex flex-wrap justify-center gap-8 md:gap-12">
      {stats.map((stat, index) => (
        <motion.div
          key={stat.label}
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: index * 0.08 }}
          className="text-center"
        >
          <div
            className={`text-3xl md:text-4xl font-bold mb-1 tabular-nums ${
              isAi ? 'text-green-400' : 'text-primary-600'
            }`}
          >
            {stat.value}
          </div>
          <div className={`text-sm ${isAi ? 'text-stone-400' : 'text-stone-600'}`}>{stat.label}</div>
        </motion.div>
      ))}
    </div>
  )
}
